import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, PhoneCall } from 'lucide-react';
import Screen from '../../demo/Screen';
import './Levels.css';

/*
 * Four plans from the client's pricing sheet (2026-10; its "Essential" column
 * is deliberately left out). No prices on the page (client, 2026-10-24:
 * "pricing should not be revealed"), so none are kept here either. Each card
 * leads with the plan name, then only what it adds to the plan before it
 * (the sheet's features are cumulative, unit-tested), so everything fits in
 * the cards with no separate table.
 */
export const PLANS = [
  {
    key: 'starter',
    name: 'Starter',
    title: 'Start with Smart Reports',
    bestFor: 'Independent centres starting preventive health checks or shifting B2C',
  },
  {
    key: 'growth',
    name: 'Growth',
    title: 'Report + digital access for patients',
    bestFor: 'Brands scaling preventive health',
  },
  {
    key: 'enterprise',
    name: 'Enterprise',
    title: 'Patient engagement at scale',
    bestFor: 'Revenue growth and retention',
  },
  {
    key: 'pro',
    name: 'Pro',
    title: 'Full platform with customisation',
    bestFor: 'Chains and multi-location labs, hospitals with wellness programs',
  },
];

const ALL = ['starter', 'growth', 'enterprise', 'pro'];
const GROWTH_UP = ['growth', 'enterprise', 'pro'];
const ENTERPRISE_UP = ['enterprise', 'pro'];
const PRO_ONLY = ['pro'];

/* Feature rows exactly as ticked on the pricing sheet. */
export const FEATURE_GROUPS = [
  {
    key: 'pdf',
    label: 'PDF Smart Reports',
    rows: [
      { key: 'descriptive', label: 'Descriptive reports (Compact + Total)', plans: ALL },
      { key: 'personal-path', label: 'Personal Smart Reports (Pathology)', plans: ALL },
      {
        key: 'personal-multi',
        label: 'Personal Smart Reports (Pathology + Radiology + Microbiology)',
        plans: ENTERPRISE_UP,
      },
    ],
  },
  {
    key: 'retention',
    label: 'Retention booster',
    note: 'increase engagement',
    rows: [
      { key: 'web-app', label: 'Web App (mobile-friendly reports)', plans: GROWTH_UP },
      { key: 'body-summary', label: 'Body Summary + Health snapshot', plans: GROWTH_UP },
      { key: 'cover', label: 'Cover Page + Promo Links + Expert panel', plans: GROWTH_UP },
      { key: 'trends', label: 'Trends analysis + Journey till date', plans: GROWTH_UP },
      { key: 'language', label: 'Language support', plans: ENTERPRISE_UP },
    ],
  },
  {
    key: 'intelligence',
    label: 'Intelligence layer',
    rows: [
      { key: 'consolidated', label: 'Consolidated health summary', plans: ENTERPRISE_UP },
      { key: 'risk', label: 'Predictive risk score', plans: ENTERPRISE_UP },
      {
        key: 'recommendations',
        label: 'Personalised recommendations (test follow-ups + diet plan)',
        plans: ENTERPRISE_UP,
      },
      { key: 'videos', label: 'Smart AI Videos', plans: PRO_ONLY },
    ],
  },
  {
    key: 'custom',
    label: 'Customisation',
    rows: [
      { key: 'content', label: 'Content customisation', plans: GROWTH_UP },
      { key: 'design', label: 'Design customisation (up to 2 man-days)', plans: PRO_ONLY },
    ],
  },
];

/* What a plan adds to the one before it (all of Starter's for Starter). */
export function addedFeatures(index) {
  const plan = PLANS[index].key;
  const before = index > 0 ? PLANS[index - 1].key : null;
  return FEATURE_GROUPS.flatMap((g) => g.rows).filter(
    (row) => row.plans.includes(plan) && !(before && row.plans.includes(before)),
  );
}

function PlanCard({ plan, index, selected, onSelect }) {
  const { key, name, title, bestFor } = plan;
  return (
    <article
      className={`card lvl-card${selected ? ' is-selected' : ''}`}
      aria-labelledby={`lvl-name-${key}`}
    >
      <h2 id={`lvl-name-${key}`} className="lvl-card__name">
        {name}
      </h2>
      <p className="lvl-card__title">{title}</p>
      <div className="lvl-feats">
        <p className="lvl-feats__lead">{index === 0 ? 'Includes' : `Everything in ${PLANS[index - 1].name}, plus`}</p>
        <ul>
          {addedFeatures(index).map((row) => (
            <li key={row.key} data-feature={row.key}>
              <span className="lvl-tick" aria-hidden="true">
                <Check size={11} strokeWidth={3.2} />
              </span>
              {row.label}
            </li>
          ))}
        </ul>
      </div>
      <p className="lvl-card__best">
        <span>Works best for</span> {bestFor}
      </p>
      <button
        type="button"
        className={`btn lvl-card__cta${selected ? ' btn--primary' : ''}`}
        aria-current={selected ? 'true' : undefined}
        aria-label={`Discuss this plan: ${name}`}
        data-trapezium-id={`packages.levels.discuss-${key}`}
        onClick={() => onSelect(key)}
      >
        Discuss this plan
        <ArrowRight size={16} aria-hidden="true" />
      </button>
    </article>
  );
}

export default function Levels() {
  const [selected, setSelected] = useState('growth');
  const plan = PLANS.find((p) => p.key === selected);

  return (
    <Screen id="packages.levels" title="Packages" className="lvl">
      <h1 className="headline lvl-headline">Choose the reporting experience that fits your goals.</h1>

      <div className="lvl-grid">
        {PLANS.map((p, i) => (
          <PlanCard key={p.key} plan={p} index={i} selected={p.key === selected} onSelect={setSelected} />
        ))}
      </div>

      <div className="card lvl-selected">
        <p className="lvl-selected__status" role="status" data-trapezium-state={`plan-${selected}`}>
          Your selected plan <span className="lvl-selected__pill">{plan.name}</span>
        </p>
        <p className="lvl-selected__note">
          All reports are white-labelled with ‘powered by NirogGyan’ in the footer.
        </p>
        <Link
          to={`/next-step?plan=${selected}`}
          className="btn lvl-talk"
          data-trapezium-id="packages.levels.talk-to-team"
        >
          <PhoneCall size={20} strokeWidth={1.9} aria-hidden="true" />
          Talk to our team
        </Link>
      </div>
    </Screen>
  );
}
